import { createContext, useState, useMemo } from "react";
import {
  createTheme,
  alpha,
  Theme,
  ThemeOptions,
  PaletteColor,
  SimplePaletteColorOptions,
} from "@mui/material/styles";

// =========================================================================
// 1. MODULE AUGMENTATION (Tells TypeScript about our custom properties)
// =========================================================================

declare module "@mui/material/styles" {
  interface Palette {
    neutral: PaletteColor;
  }
  interface PaletteOptions {
    neutral?: SimplePaletteColorOptions;
  }
  interface Theme {
    appSizes: typeof SHARED_APP_SIZES;
    appRadius: typeof SHARED_APP_RADIUS;
    appZIndex: typeof SHARED_APP_Z_INDEX;
    appGradients: TokenGradients;
  }
  interface ThemeOptions {
    appSizes?: typeof SHARED_APP_SIZES;
    appRadius?: typeof SHARED_APP_RADIUS;
    appZIndex?: typeof SHARED_APP_Z_INDEX;
    appGradients?: TokenGradients;
  }
}

declare module "@mui/material/Button" {
  interface ButtonPropsVariantOverrides {
    gradient: true;
  }
}

// =========================================================================
// 2. SHARED CONSTANTS
// =========================================================================

const SHARED_APP_SIZES = {
  spacingUnit: 8,
  xs: 4,
  sm: 8,
  md: 16,
  lg: 24,
  xl: 32,
  xxl: 48,

  // Font Sizes
  fontXs: 10,
  fontSm: 12,
  fontBase: 14,
  fontMd: 16,
  fontLg: 18,
  fontXl: 20,
  fontXxl: 24,
  fontH3: 28,
  fontH2: 32,
  fontH1: 40,

  // Layout
  sidebarWidth: 260,
  headerHeight: 64,
  buttonHeightSmall: 32,
  buttonHeightMedium: 42,
  buttonHeightLarge: 52,
  inputHeight: 46,
};

const SHARED_APP_RADIUS = {
  none: "0px",
  xs: "4px",
  sm: "6px",
  md: "10px",
  lg: "16px",
  xl: "24px",
  circular: "50%",
};

const SHARED_APP_Z_INDEX = {
  dropdown: 1000,
  sticky: 1020,
  backdrop: 1030,
  modal: 1050,
  popover: 1060,
  tooltip: 1070,
};

// =========================================================================
// 3. TYPES
// =========================================================================

export interface TokenScale {
  [key: number]: string;
}

export interface TokenGradients {
  primary: string;
  success: string;
  info: string;
  warning: string;
  ocean: string;
  glass: string;
}

export interface AppTokens {
  grey: TokenScale;
  primary: TokenScale;
  green: TokenScale;
  red: TokenScale;
  blue: TokenScale;
  gradients: TokenGradients;
  sizes: typeof SHARED_APP_SIZES;
  radius: typeof SHARED_APP_RADIUS;
  zIndex: typeof SHARED_APP_Z_INDEX;
}

// =========================================================================
// 4. COLOR TOKENS
// =========================================================================

export const tokens = (mode: "dark" | "light"): AppTokens => ({
  ...(mode === "dark"
    ? {
        // --- DARK MODE FIRST ---
        grey: {
          100: "#f8fafc",
          200: "#f1f5f9",
          300: "#e2e8f0",
          400: "#cbd5e1",
          500: "#94a3b8",
          600: "#64748b",
          700: "#475569",
          800: "#334155",
          900: "#1e293b",
        },
        primary: {
          // Deep Slate tones replacing the old purple/navy
          100: "#cbd5e1",
          200: "#94a3b8",
          300: "#64748b",
          400: "#1E293B", // Cards/Paper background
          500: "#0F172A", // Main App Background
          600: "#020617",
          700: "#000000",
          800: "#000000",
          900: "#000000",
        },
        green: {
          100: "#dcfce7",
          200: "#bbf7d0",
          300: "#86efac",
          400: "#4ade80",
          500: "#22C55E", // Main vibrant green
          600: "#16a34a",
          700: "#15803d",
          800: "#166534",
          900: "#14532d",
        },
        red: {
          // Clean, crisp Red for Errors
          100: "#fee2e2",
          200: "#fecaca",
          300: "#fca5a5",
          400: "#f87171",
          500: "#EF4444", // Pure Error Red
          600: "#dc2626",
          700: "#b91c1c",
        },
        blue: {
          // Sky Blue (Twitter/X Style) for Links/Primary Actions
          100: "#e0f2fe",
          200: "#bae6fd",
          300: "#7dd3fc",
          400: "#38bdf8",
          500: "#1D9BF0", // X Brand Blue
          600: "#0284c7",
          700: "#0369a1",
        },
        gradients: {
          primary: "linear-gradient(135deg, #0284c7 0%, #38bdf8 100%)", // Sky Blue
          success: "linear-gradient(135deg, #16a34a 0%, #4ade80 100%)", // Vibrant Green
          info: "linear-gradient(135deg, #0369a1 0%, #7dd3fc 100%)",
          warning: "linear-gradient(135deg, #dc2626 0%, #f87171 100%)", // Crisp Red
          ocean: "linear-gradient(135deg, #06b6d4 0%, #1D9BF0 100%)", // Cyan to Sky Blue (No Purple)
          glass:
            "linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)",
        },
      }
    : {
        // --- LIGHT MODE --- (Inverted scales)
        grey: {
          100: "#1e293b", // Darkest text
          200: "#334155",
          300: "#475569",
          400: "#64748b",
          500: "#94a3b8",
          600: "#cbd5e1",
          700: "#e2e8f0",
          800: "#f1f5f9",
          900: "#f8fafc", // Lightest
        },
        primary: {
          100: "#000000",
          200: "#020617",
          300: "#0F172A",
          400: "#1E293B",
          500: "#64748b",
          600: "#94a3b8",
          700: "#cbd5e1",
          800: "#f1f5f9", // Light surface
          900: "#f8fafc", // Light background
        },
        green: {
          100: "#14532d", // Darkest green
          200: "#166534",
          300: "#15803d",
          400: "#16a34a",
          500: "#22C55E", // Main vibrant green
          600: "#4ade80",
          700: "#86efac",
          800: "#bbf7d0",
          900: "#dcfce7",
        },
        red: {
          100: "#7f1d1d",
          200: "#991b1b",
          300: "#b91c1c",
          400: "#dc2626",
          500: "#EF4444",
          600: "#f87171",
          700: "#fca5a5",
        },
        blue: {
          100: "#0c4a6e",
          200: "#075985",
          300: "#0369a1",
          400: "#0284c7",
          500: "#1D9BF0",
          600: "#38bdf8",
          700: "#7dd3fc",
        },
        gradients: {
          primary: "linear-gradient(135deg, #1D9BF0 0%, #0284c7 100%)", // Sky Blue
          success: "linear-gradient(135deg, #22C55E 0%, #16a34a 100%)", // Vibrant Green
          info: "linear-gradient(135deg, #38bdf8 0%, #1D9BF0 100%)",
          warning: "linear-gradient(135deg, #f87171 0%, #EF4444 100%)",
          ocean: "linear-gradient(135deg, #06b6d4 0%, #1D9BF0 100%)",
          glass:
            "linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%)",
        },
      }),
  sizes: SHARED_APP_SIZES,
  radius: SHARED_APP_RADIUS,
  zIndex: SHARED_APP_Z_INDEX,
});

// =========================================================================
// 5. THEME SETTINGS GENERATOR
// =========================================================================

export const themeSettings = (mode: "dark" | "light"): ThemeOptions => {
  const allTokens = tokens(mode);

  return {
    palette: {
      mode: mode,
      ...(mode === "dark"
        ? {
            // Dark Mode
            primary: {
              main: allTokens.blue[500], // Swapped to use Sky Blue for primary actions/links
              light: allTokens.blue[400],
              dark: allTokens.blue[600],
            },
            secondary: { main: allTokens.green[500] },
            neutral: {
              dark: allTokens.grey[700],
              main: allTokens.grey[500],
              light: allTokens.grey[100],
            },
            background: {
              default: allTokens.primary[500], // Still uses your designated background tokens
              paper: allTokens.primary[400],
            },
            success: { main: allTokens.green[500] },
            error: { main: allTokens.red[500] },
            warning: { main: "#F59E0B" }, // Clean amber warning (better contrast than red for warning)
            info: { main: allTokens.blue[500] },
          }
        : {
            // Light Mode
            primary: {
              main: allTokens.blue[500],
              light: allTokens.blue[400],
              dark: allTokens.blue[600],
            },
            secondary: { main: allTokens.green[500] },
            neutral: {
              dark: allTokens.grey[700],
              main: allTokens.grey[500],
              light: allTokens.grey[100],
            },
            background: {
              default: "#F8FAFC", // Clean Slate-tinted off-white
              paper: "#ffffff",
            },
            success: { main: allTokens.green[500] },
            error: { main: allTokens.red[500] },
            warning: { main: "#F59E0B" },
            info: { main: allTokens.blue[500] },
          }),
    },

    appSizes: allTokens.sizes,
    appRadius: allTokens.radius,
    appZIndex: allTokens.zIndex,
    appGradients: allTokens.gradients,

    spacing: allTokens.sizes.spacingUnit,
    shape: {
      borderRadius: parseInt(allTokens.radius.sm.replace("px", ""), 10),
    },

    typography: {
      fontFamily: ["Source Sans 3", "Inter", "sans-serif"].join(","),
      fontSize: allTokens.sizes.fontBase,
      h1: {
        fontSize: `${allTokens.sizes.fontH1}px`,
        fontWeight: 700,
        lineHeight: 1.2,
      },
      h2: {
        fontSize: `${allTokens.sizes.fontH2}px`,
        fontWeight: 700,
        lineHeight: 1.3,
      },
      h3: {
        fontSize: `${allTokens.sizes.fontH3}px`,
        fontWeight: 600,
        lineHeight: 1.4,
      },
      h4: {
        fontSize: `${allTokens.sizes.fontXxl}px`,
        fontWeight: 600,
      },
      h5: {
        fontSize: `${allTokens.sizes.fontLg}px`,
        fontWeight: 600,
      },
      h6: {
        fontSize: `${allTokens.sizes.fontBase}px`,
        fontWeight: 600,
        letterSpacing: "0.5px",
      },
      subtitle1: {
        fontSize: `${allTokens.sizes.fontMd}px`,
        fontWeight: 500,
        color: mode === "dark" ? allTokens.grey[100] : allTokens.grey[500],
      },
      subtitle2: {
        fontSize: `${allTokens.sizes.fontSm}px`,
        fontWeight: 600,
        letterSpacing: "0.5px",
        textTransform: "uppercase",
        color: mode === "dark" ? allTokens.grey[300] : allTokens.grey[600],
      },
      body1: {
        fontSize: `${allTokens.sizes.fontBase}px`,
      },
      body2: {
        fontSize: `${allTokens.sizes.fontSm}px`,
      },
      button: {
        fontSize: `${allTokens.sizes.fontBase}px`,
        fontWeight: 600,
        textTransform: "none",
      },
    },

    components: {
      MuiTableCell: {
        styleOverrides: {
          head: ({ theme }) => {
            const t = theme as Theme;
            return {
              backgroundColor:
                mode === "dark" ? t.palette.background.default : "#f1f5f9",
              color:
                mode === "dark" ? allTokens.grey[100] : allTokens.grey[100],
              padding: `${t.spacing(1.5)} ${t.spacing(2)}`,
              borderBottom: `1px solid ${mode === "dark" ? allTokens.primary[300] : "#e2e8f0"}`,
              textTransform: "uppercase",
              fontSize: t.typography.pxToRem(12),
              letterSpacing: "1px",
            };
          },
          body: ({ theme }) => {
            const t = theme as Theme;
            return {
              padding: `${t.spacing(1.5)} ${t.spacing(2)}`,
              borderBottom: `1px solid ${mode === "dark" ? allTokens.primary[300] : "#e2e8f0"}`,
              fontSize: t.typography.pxToRem(14),
            };
          },
        },
      },

      MuiButton: {
        variants: [
          {
            props: { variant: "gradient" },
            style: {
              background: allTokens.gradients.ocean,
              color: "#fff",
              boxShadow: "0px 4px 14px rgba(29, 155, 240, 0.2)",
              border: "none",
              "&:hover": {
                boxShadow: "0px 6px 20px rgba(29, 155, 240, 0.4)",
                transform: "translateY(-2px)",
              },
            },
          },
          {
            props: { variant: "gradient", color: "error" },
            style: {
              background: allTokens.gradients.warning,
              "&:hover": { boxShadow: "0px 6px 20px rgba(239, 68, 68, 0.4)" },
            },
          },
          {
            props: { variant: "gradient", color: "info" },
            style: {
              background: allTokens.gradients.info,
              "&:hover": { boxShadow: "0px 6px 20px rgba(29, 155, 240, 0.4)" },
            },
          },
        ],
        styleOverrides: {
          root: ({ theme, ownerState }) => {
            const t = theme as Theme;
            return {
              borderRadius: t.appRadius.sm,
              minHeight: t.appSizes.buttonHeightMedium,
              transition: "all 0.3s ease",
              ...(ownerState.size === "small" && {
                minHeight: t.appSizes.buttonHeightSmall,
                fontSize: 12,
              }),
              ...(ownerState.size === "large" && {
                minHeight: t.appSizes.buttonHeightLarge,
                fontSize: 16,
                padding: "0 32px",
              }),
            };
          },
        },
      },

      MuiChip: {
        styleOverrides: {
          root: ({ theme }) => {
            const t = theme as Theme;

            return {
              borderRadius: t.appRadius.sm,
              fontWeight: 600,
              fontSize: 12,
            };
          },
        },

        variants: [
          {
            props: {
              variant: "filled",
              color: "success",
            },
            style: {
              backgroundColor: alpha(allTokens.green[500], 0.15),
              color:
                mode === "dark" ? allTokens.green[300] : allTokens.green[700],
            },
          },
          {
            props: {
              variant: "filled",
              color: "error",
            },
            style: {
              backgroundColor: alpha(allTokens.red[500], 0.15),
              color: mode === "dark" ? allTokens.red[300] : allTokens.red[700],
            },
          },
          {
            props: {
              variant: "filled",
              color: "warning",
            },
            style: {
              backgroundColor: alpha("#F59E0B", 0.15),
              color: mode === "dark" ? "#FCD34D" : "#B45309",
            },
          },
        ],
      },

      MuiCard: {
        styleOverrides: {
          root: ({ theme }) => {
            const t = theme as Theme;
            return {
              borderRadius: t.appRadius.lg,
              backgroundImage: "none",
              backgroundColor:
                mode === "dark" ? allTokens.primary[400] : "#ffffff",
              boxShadow:
                mode === "dark" ? "none" : "0px 4px 12px rgba(0,0,0,0.03)",
              border:
                mode === "dark"
                  ? `1px solid ${allTokens.primary[300]}`
                  : `1px solid #e2e8f0`,
            };
          },
        },
      },

      MuiOutlinedInput: {
        styleOverrides: {
          root: ({ theme }) => {
            const t = theme as Theme;
            return {
              borderRadius: t.appRadius.sm,
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: allTokens.blue[500], // Swapped focus to X-Blue
                borderWidth: "2px",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor:
                  mode === "dark" ? allTokens.grey[300] : allTokens.grey[500],
              },
            };
          },
          input: { padding: "12px 14px" },
        },
      },

      MuiInputLabel: {
        styleOverrides: {
          root: ({ theme }) => ({
            "&.Mui-focused": {
              color: allTokens.blue[500], // Swapped to X-Blue
              fontWeight: 600,
            },
          }),
        },
      },

      MuiCssBaseline: {
        styleOverrides: (themeParam) => {
          const t = themeParam as Theme;
          return {
            body: {
              "&::-webkit-scrollbar": { width: "8px", height: "8px" },
              "&::-webkit-scrollbar-track": {
                background:
                  mode === "dark" ? t.palette.background.default : "#f1f5f9",
              },
              "&::-webkit-scrollbar-thumb": {
                background:
                  mode === "dark" ? allTokens.primary[300] : "#cbd5e1",
                borderRadius: "4px",
                "&:hover": { background: allTokens.blue[500] },
              },
            },
          };
        },
      },

      MuiCheckbox: {
        styleOverrides: {
          root: ({ theme }) => ({
            color: theme.palette.primary.main,
            "&.Mui-checked": { color: theme.palette.primary.main },
            "&.MuiCheckbox-indeterminate": {
              color: theme.palette.primary.main,
            },
          }),
        },
      },
    },
  };
};

// =========================================================================
// 6. CONTEXT & HOOKS
// =========================================================================

export const ColorModeContext = createContext({
  toggleColorMode: () => {},
});

export const useMode = (): readonly [
  Theme,
  { toggleColorMode: () => void },
] => {
  const [mode, setMode] = useState<"dark" | "light">("dark");

  const colorMode = useMemo(
    () => ({
      toggleColorMode: () =>
        setMode((prev) => (prev === "light" ? "dark" : "light")),
    }),
    [],
  );

  const theme = useMemo(() => createTheme(themeSettings(mode)), [mode]);
  return [theme, colorMode] as const;
};

export const useGradients = (mode: "dark" | "light"): TokenGradients =>
  tokens(mode).gradients;
